<template>
  <section class="forecasting-optimization-blocking-chart">
    <div class="forecasting-optimization-blocking-chart__toolbar">
      <div class="forecasting-optimization-blocking-chart__heading">
        <span class="forecasting-optimization-blocking-chart__title">Blocking Chart</span>
        <div class="forecasting-optimization-blocking-chart__metric-tabs">
          <v-chip
            label
            class="forecasting-optimization-blocking-chart__metric-chip"
            :class="{
              'forecasting-optimization-blocking-chart__metric-chip--active':
                data_breakdown_type === 'spend',
            }"
            :outlined="data_breakdown_type !== 'spend'"
            color="black"
            @click="switch_data_breakdown('spend')"
          >
            Spend
          </v-chip>
          <v-chip
            label
            class="forecasting-optimization-blocking-chart__metric-chip"
            :class="{
              'forecasting-optimization-blocking-chart__metric-chip--active':
                data_breakdown_type === 'impressions',
            }"
            :outlined="data_breakdown_type !== 'impressions'"
            color="black"
            @click="switch_data_breakdown('impressions')"
          >
            Impr.
          </v-chip>
          <v-chip
            label
            class="forecasting-optimization-blocking-chart__metric-chip"
            :class="{
              'forecasting-optimization-blocking-chart__metric-chip--active':
                data_breakdown_type === 'clicks',
            }"
            :outlined="data_breakdown_type !== 'clicks'"
            color="black"
            @click="switch_data_breakdown('clicks')"
          >
            Clicks
          </v-chip>
        </div>
      </div>
      <div class="forecasting-optimization-blocking-chart__actions">
        <div class="forecasting-optimization-blocking-chart__percentage-toggle-wrapper">
          <v-switch
            v-model="show_metric_percentages"
            class="forecasting-optimization-blocking-chart__percentage-toggle"
            label="%"
            dense
            hide-details
          ></v-switch>
        </div>
        <v-select
          v-model="data_breakdown_timing"
          :items="time_interval_options"
          class="forecasting-optimization-blocking-chart__interval"
          hide-details
          @change="reset_table"
        ></v-select>
      </div>
    </div>
    <div
      v-if="!has_required_context"
      class="forecasting-optimization-blocking-chart__empty"
    >
      No blocking chart data available.
    </div>
    <div
      v-else-if="!table_available"
      class="forecasting-optimization-blocking-chart__loading"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </div>
    <PerformanceBlockingChart
      v-else
      :tableHeader="headers"
      :tableData="processed_data"
      :selectedLevels="selected_levels"
      :tableRowClickFunction="empty_table_click"
      :numericColumns="numeric_columns"
      :currencyColumns="currency_columns"
      :dataBreakdownType="data_breakdown_type"
      :showMetricPercentages="show_metric_percentages"
    />
  </section>
</template>

<script>
import PerformanceBlockingChart from "@/components/Performance/PerformanceStats/PerformanceDataBreakdownComponent/PerformanceBlockingChart.vue";
import performance_data_header from "@/input_data/performance_table_header.json";
import api_caller from "@/javascript/data_retrieval.js";
import { ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE } from "@/constants/constants";

export default {
  name: "ForecastingOptimizationBlockingChart",
  components: {
    PerformanceBlockingChart,
  },
  props: {
    project_date_range: {
      type: Object,
      default: () => ({}),
    },
    data_levels: {
      type: Object,
      default: () => ({}),
    },
    data_filters: {
      type: Object,
      default: () => ({}),
    },
    optimization_table_data: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    performance_data: [],
    processed_data: [],
    table_available: false,
    data_breakdown_timing: "Monthly",
    data_breakdown_type: "spend",
    show_metric_percentages: true,
    time_interval_options: ["Weekly", "Bi-Weekly", "Monthly"],
    fetch_sequence: 0,
  }),
  computed: {
    top_data_level() {
      if (
        this.data_levels &&
        Array.isArray(this.data_levels["data_levels"]) &&
        this.data_levels["data_levels"].length > 0
      ) {
        return this.data_levels["data_levels"][0];
      }

      if (
        this.data_levels &&
        Array.isArray(this.data_levels["selected_levels"]) &&
        this.data_levels["selected_levels"].length > 0
      ) {
        return this.data_levels["selected_levels"][0];
      }

      return null;
    },
    selected_levels() {
      return this.top_data_level ? [this.top_data_level] : [];
    },
    start_date() {
      return this.project_date_range ? this.project_date_range["start_date"] : null;
    },
    end_date() {
      return this.project_date_range ? this.project_date_range["end_date"] : null;
    },
    has_required_context() {
      return Boolean(this.top_data_level && this.start_date && this.end_date);
    },
    headers() {
      if (!this.has_required_context) {
        return [];
      }

      let dataTableHeader = [];
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const end = new Date(this.end_date);
      const startParts = this.start_date.split("-");
      let dateCursor = new Date(
        startParts[0],
        parseInt(startParts[1]) - 1,
        parseInt(startParts[2])
      );
      const timing = this.data_breakdown_timing.toLowerCase();

      if (timing === "weekly" || timing === "bi-weekly") {
        const daySkip = timing === "weekly" ? 7 : 14;
        for (; dateCursor <= end; dateCursor.setDate(dateCursor.getDate() + daySkip)) {
          const dateLabel = dateCursor.toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
          dataTableHeader.push({
            text: dateLabel,
            value: dateLabel,
            sortable: true,
            desc: "",
          });
        }
      } else if (timing === "monthly") {
        for (; dateCursor <= end; dateCursor.setMonth(dateCursor.getMonth() + 1)) {
          const monthLabel = months[dateCursor.getMonth()] + " " + dateCursor.getFullYear();
          dataTableHeader.push({
            text: monthLabel,
            value: monthLabel,
            sortable: true,
            desc: "",
          });
        }
      } else if (timing === "quarterly") {
        for (; dateCursor <= end; dateCursor.setMonth(dateCursor.getMonth() + 3)) {
          const quarter = Math.floor((dateCursor.getMonth() + 3) / 3);
          const quarterLabel = "Q" + quarter + " " + dateCursor.getFullYear();
          dataTableHeader.push({
            text: quarterLabel,
            value: quarterLabel,
            sortable: false,
            desc: "",
          });
        }
      } else {
        for (; dateCursor <= end; dateCursor.setFullYear(dateCursor.getFullYear() + 1)) {
          const yearLabel = dateCursor.getFullYear().toString();
          dataTableHeader.push({
            text: yearLabel,
            value: yearLabel,
            sortable: true,
            desc: "",
          });
        }
      }

      dataTableHeader.unshift(
        performance_data_header["table_header_levels"][this.top_data_level] || {
          text: this.top_data_level,
          value: this.top_data_level,
          sortable: true,
          desc: "",
        }
      );

      return dataTableHeader;
    },
    currency_columns() {
      return this.data_breakdown_type === "spend" ? this.metric_columns : [];
    },
    numeric_columns() {
      return this.data_breakdown_type === "spend" ? [] : this.metric_columns;
    },
    metric_columns() {
      return this.headers
        .filter((header) => !this.selected_levels.includes(header.value))
        .map((header) => header.value);
    },
    optimization_spend_change_lookup() {
      if (!this.top_data_level || !Array.isArray(this.optimization_table_data)) {
        return {};
      }

      return this.optimization_table_data.reduce((lookup, row) => {
        if (!row || row[this.top_data_level] == null) {
          return lookup;
        }

        const spendChange = this.parse_spend_change(row["spend_change"]);
        if (spendChange === null) {
          return lookup;
        }

        lookup[this.normalize_lookup_key(row[this.top_data_level])] = spendChange;
        return lookup;
      }, {});
    },
  },
  mounted() {
    this.reset_table();
  },
  watch: {
    project_date_range: {
      handler: function () {
        this.reset_table();
      },
      deep: true,
    },
    data_levels: {
      handler: function () {
        this.reset_table();
      },
      deep: true,
    },
    data_filters: {
      handler: function () {
        this.reset_table();
      },
      deep: true,
    },
    optimization_table_data: {
      handler: function () {
        if (this.performance_data.length > 0) {
          this.process_breakdown_data();
        }
      },
      deep: true,
    },
  },
  methods: {
    switch_data_breakdown: function (type) {
      this.data_breakdown_type = type;
      this.process_breakdown_data();
    },
    reset_table: async function () {
      this.fetch_sequence += 1;
      const currentFetchSequence = this.fetch_sequence;
      this.table_available = false;
      this.performance_data = [];
      this.processed_data = [];

      if (!this.has_required_context) {
        return;
      }

      const dateRange = {
        start_date: this.start_date,
        end_date: this.end_date,
      };
      const breakdownData = await this.$auth
        .getTokenSilently()
        .then((token) => {
          return api_caller.get_breakdown_data(
            token,
            this.$project.get_val(),
            this.data_breakdown_timing.toLowerCase(),
            dateRange,
            this.selected_levels,
            this.data_filters || {}
          );
        })
        .then((resp) => {
          if (resp && resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch(() => {
          return false;
        });

      if (currentFetchSequence !== this.fetch_sequence) {
        return;
      }

      if (!breakdownData) {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        return;
      }

      this.performance_data = breakdownData;
      this.process_breakdown_data();
      this.table_available = true;
    },
    process_breakdown_data: function () {
      this.processed_data = this.performance_data.map((sourceRow) => {
        let row = {};
        let metricTotals = {
          spend: 0,
          impressions: 0,
          clicks: 0,
        };
        const spendChange = this.get_row_spend_change(sourceRow);

        for (const column in sourceRow) {
          if (this.selected_levels.includes(column)) {
            row[column] = sourceRow[column];
          } else if (
            sourceRow[column] &&
            typeof sourceRow[column] === "object" &&
            Object.prototype.hasOwnProperty.call(sourceRow[column], "spend")
          ) {
            const adjustedMetrics = this.get_adjusted_metrics(
              sourceRow[column],
              spendChange
            );
            row[column] = adjustedMetrics[this.data_breakdown_type];
            this.add_metric_totals(metricTotals, adjustedMetrics);
          } else {
            row[column] = this.apply_spend_change(sourceRow[column], spendChange);
          }
        }
        row["__metricTotals"] = metricTotals;

        return row;
      });
    },
    get_adjusted_metrics: function (value, spendChange) {
      if (!value || typeof value !== "object") {
        return {
          spend: 0,
          impressions: 0,
          clicks: 0,
        };
      }

      const rawSpend = this.parse_numeric_value(value["spend"]);
      const rawImpressions = this.parse_numeric_value(value["impressions"]);
      const rawClicks = this.parse_numeric_value(value["clicks"]);
      const adjustedSpend = this.apply_spend_change(rawSpend, spendChange);
      const derivedImpressions = this.get_derived_impressions(
        adjustedSpend,
        rawSpend,
        rawImpressions
      );
      const derivedClicks = this.get_derived_clicks(
        adjustedSpend,
        rawSpend,
        rawClicks
      );

      return {
        spend: this.get_numeric_or_zero(adjustedSpend),
        impressions: this.get_numeric_or_zero(derivedImpressions),
        clicks: this.get_numeric_or_zero(derivedClicks),
      };
    },
    get_derived_impressions: function (adjustedSpend, rawSpend, rawImpressions) {
      if (
        rawSpend === null ||
        rawSpend === 0 ||
        rawImpressions === null ||
        rawImpressions === 0 ||
        adjustedSpend === null
      ) {
        return rawImpressions;
      }

      const originalCpm = (rawSpend / rawImpressions) * 1000;
      if (!Number.isFinite(originalCpm) || originalCpm === 0) {
        return rawImpressions;
      }

      return (adjustedSpend / originalCpm) * 1000;
    },
    get_derived_clicks: function (adjustedSpend, rawSpend, rawClicks) {
      if (
        rawSpend === null ||
        rawSpend === 0 ||
        rawClicks === null ||
        rawClicks === 0 ||
        adjustedSpend === null
      ) {
        return rawClicks;
      }

      const originalCpc = rawSpend / rawClicks;
      if (!Number.isFinite(originalCpc) || originalCpc === 0) {
        return rawClicks;
      }

      return adjustedSpend / originalCpc;
    },
    add_metric_totals: function (metricTotals, value) {
      if (!value || typeof value !== "object") {
        return;
      }

      const spend = this.parse_numeric_value(value["spend"]);
      const impressions = this.parse_numeric_value(value["impressions"]);
      const clicks = this.parse_numeric_value(value["clicks"]);

      if (spend !== null) {
        metricTotals["spend"] += spend;
      }
      if (impressions !== null) {
        metricTotals["impressions"] += impressions;
      }
      if (clicks !== null) {
        metricTotals["clicks"] += clicks;
      }
    },
    get_row_spend_change: function (row) {
      if (!row || !this.top_data_level || row[this.top_data_level] == null) {
        return null;
      }

      const lookupKey = this.normalize_lookup_key(row[this.top_data_level]);
      if (
        Object.prototype.hasOwnProperty.call(
          this.optimization_spend_change_lookup,
          lookupKey
        )
      ) {
        return this.optimization_spend_change_lookup[lookupKey];
      }

      return null;
    },
    apply_spend_change: function (value, spendChange) {
      const numericValue = this.parse_numeric_value(value);
      if (spendChange === null || numericValue === null) {
        return value;
      }

      return numericValue * (1 + spendChange);
    },
    get_numeric_or_zero: function (value) {
      const numericValue = this.parse_numeric_value(value);
      return numericValue === null ? 0 : numericValue;
    },
    parse_spend_change: function (value) {
      const numericValue = this.parse_numeric_value(value);
      if (numericValue === null) {
        return null;
      }

      return Math.abs(numericValue) > 1 ? numericValue / 100 : numericValue;
    },
    parse_numeric_value: function (value) {
      if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
      }

      if (typeof value === "string") {
        const parsedValue = Number(value.replace(/[$,%\s,]/g, ""));
        return Number.isNaN(parsedValue) ? null : parsedValue;
      }

      return null;
    },
    normalize_lookup_key: function (value) {
      return String(value).trim().toLowerCase();
    },
    empty_table_click: function () {},
  },
};
</script>

<style scoped>
.forecasting-optimization-blocking-chart {
  margin-top: 28px;
  width: 100%;
}

.forecasting-optimization-blocking-chart__toolbar {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin: 0 12px 4px;
}

.forecasting-optimization-blocking-chart__heading {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.forecasting-optimization-blocking-chart__title {
  color: #222222;
  font-family: "Poppins" !important;
  font-size: 15px;
  font-weight: 600;
}

.forecasting-optimization-blocking-chart__metric-tabs {
  display: flex;
  gap: 2px;
}

.forecasting-optimization-blocking-chart__metric-chip {
  border-radius: 3px !important;
  color: #111111 !important;
  font-family: "Poppins" !important;
  font-size: 12px;
  font-weight: 600;
  height: 28px !important;
  justify-content: center;
  margin-left: 1px;
  margin-right: 1px;
  width: 150px;
}

.forecasting-optimization-blocking-chart__metric-chip--active {
  color: #FFFFFF !important;
}

.forecasting-optimization-blocking-chart__actions {
  align-items: center;
  display: flex;
  gap: 12px;
  height: 48px;
  justify-content: flex-end;
  margin-left: auto;
}

.forecasting-optimization-blocking-chart__interval {
  align-items: center;
  display: flex;
  flex: 0 0 200px;
  font-family: "Poppins" !important;
  font-size: 0.9 em;
  font-weight: 400;
  height: 48px;
  max-width: 200px;
}

.forecasting-optimization-blocking-chart__percentage-toggle-wrapper {
  align-items: center;
  display: flex;
  height: 48px;
  transform: translateY(12px);
}

.forecasting-optimization-blocking-chart__percentage-toggle {
  flex: 0 0 auto;
  font-family: "Poppins" !important;
  font-weight: 600;
  min-width: 52px;
}

.forecasting-optimization-blocking-chart__percentage-toggle.v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.forecasting-optimization-blocking-chart__interval ::v-deep .v-input__control,
.forecasting-optimization-blocking-chart__interval ::v-deep .v-input__slot {
  align-items: center;
  display: flex;
  margin: 0;
}

.forecasting-optimization-blocking-chart__loading,
.forecasting-optimization-blocking-chart__empty {
  align-items: center;
  color: #6B7280;
  display: flex;
  font-family: "Poppins" !important;
  font-size: 14px;
  font-weight: 500;
  justify-content: center;
  min-height: 220px;
  width: 100%;
}
</style>
